import type { Breakpoint } from '@ctw/config/zui';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Pagination } from '@ctw/shared/components/pagination/pagination';
import { SimplePagination } from '@ctw/shared/components/pagination/simple-pagination';
import {
  TelemetryBoundary,
  type useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { isFhirResource } from '@ctw/shared/utils/fhir/resource-mappings';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { VoidValue } from '@ctw/shared/utils/types';
import { isEmpty, isFunction } from 'lodash-es';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { onlyText } from 'react-children-utilities';
import { ExpandList } from '../pagination/expand-list';

export type LegacyTableSpacing = 'relaxed' | 'tight';

export interface LegacyMinRecordItem {
  key: string | number;
}

export type LegacyDataIndexSpecified<_T extends LegacyMinRecordItem> = {
  // biome-ignore lint/suspicious/noExplicitAny: No explanation was provided. Please be a good citizen and add one!
  dataIndex: any;
  render?: never;
};
export type LegacyRenderSpecified<T extends LegacyMinRecordItem> = {
  dataIndex?: never;
  renderClassName?: (row: T) => ClassName;
  render: (row: T, isStacked?: boolean) => ReactNode;
};

export type LegacyTableColumn<T extends LegacyMinRecordItem> = {
  title: ReactNode;
  titleVisuallyHidden?: boolean;
  className?: ClassName;
  headerClassName?: ClassName;
  widthPercent?: number;
  minWidth?: number | string;
  maxWidth?: number | string;
  width?: number | string;
} & (LegacyDataIndexSpecified<T> | LegacyRenderSpecified<T>);

export type LegacyRowActionsProps<T extends LegacyMinRecordItem> = {
  record: T;
  onSuccess?: () => void;
  stacked?: boolean;
};

export type LegacyRowActionsProp<T extends LegacyMinRecordItem> = (
  props: LegacyRowActionsProps<T>,
) => ReactNode;

export type LegacyTablePagination =
  | {
      type: 'show-more';
      pageSize: number;
    }
  | {
      type: 'next-previous';
      hasNext: boolean;
      onPageChange?: (page: number) => void;
    }
  | {
      type: 'pages';
      pageSize: number;
      total: number;
      onPageChange?: (page: number) => void;
    };

export type LegacyTableProps<T extends LegacyMinRecordItem> = {
  testId?: string;
  id?: string;
  title: string;
  titleVisuallyHidden?: boolean;
  className?: ClassName;
  containerClassName?: ClassName;
  columns: Array<LegacyTableColumn<T>>;
  emptyMessage?: string | ReactElement;
  emptyStateShowHeaders?: boolean;
  pagination?: LegacyTablePagination | undefined;
  loading?: boolean;
  pageSize?: number;
  records: Array<T>;
  showTableHead?: boolean;
  stacked?: boolean;
  stackedBreakpoint?: Breakpoint;
  stackedColumnHeaders?: boolean;
  rowHeaders?: Array<string>;
  spacing?: LegacyTableSpacing;
  stickyHeaders?: boolean;
} & Pick<
  LegacyTableRowsProps<T>,
  'handleRowClick' | 'canClickRow' | 'RowActions' | 'getRowClassName' | 'onCmdOrCtrlClick'
>;

export const LegacyTable = <T extends LegacyMinRecordItem>({
  testId,
  id,
  title,
  className,
  containerClassName,
  columns,
  records,
  loading = false,
  emptyMessage = 'No records found',
  emptyStateShowHeaders = false,
  showTableHead = true,
  stacked = false,
  stackedBreakpoint = 'xs',
  stackedColumnHeaders = false,
  handleRowClick,
  canClickRow,
  onCmdOrCtrlClick,
  RowActions,
  getRowClassName,
  pagination,
  rowHeaders,
  spacing = 'relaxed',
  stickyHeaders = false,
}: LegacyTableProps<T>) => {
  const containerRef = useRef<HTMLTableElement>(null);
  const [internalCurrentPage, setInternalInternalCurrentPage] = useState(1);
  const [isStacked, setStacked] = useState(stacked);

  const [count, setCount] = useState(
    pagination?.type === 'show-more' || pagination?.type === 'pages'
      ? pagination.pageSize
      : records.length,
  );
  const { breakpoints } = useContainerQuery({ containerRef });
  const setCurrentPage = (page: number) => {
    if (pagination?.type === 'next-previous' || pagination?.type === 'pages') {
      pagination.onPageChange?.(page);
    }
    setInternalInternalCurrentPage(page);
  };

  useEffect(() => {
    if (breakpoints.isAtMost[stackedBreakpoint]) {
      setStacked(true);
    } else {
      setStacked(stacked);
    }
  }, [breakpoints.isAtMost, stacked, stackedBreakpoint]);

  const hasData = !loading && records.length > 0;

  useEffect(() => {
    if (pagination?.type === 'show-more' && hasData) {
      setCount(Math.min(Math.max(count, pagination.pageSize), records.length));
    } else {
      setCount(records.length);
    }
  }, [records, count, pagination, hasData]);

  return (
    <ErrorBoundary boundaryName="Table" initialContext={{ tableTitle: title }}>
      {() => (
        <div
          className={twx(
            'space-y-4',
            {
              'table-stacked': isStacked,
            },
            containerClassName,
          )}
        >
          <div className={twx('relative overflow-hidden border-divider-main border-b', className)}>
            <div
              className={twx('scroll-shadows overflow-x-auto', {
                'overflow-x-hidden': isStacked,
                'h-full overflow-y-auto': stickyHeaders && !isStacked,
              })}
            >
              <table
                ref={containerRef}
                className={tw`w-full table-auto border-collapse`}
                data-testid={testId}
              >
                {hasData && (
                  <colgroup className={twx({ hidden: isStacked })}>
                    {columns.map((column, index) => (
                      <col
                        // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
                        key={`table-colgroup-col-${id}-${title}-${onlyText(column.title)}-${index}`}
                        className={twx(column.className)}
                        width={column.widthPercent ? `${column.widthPercent}%` : undefined}
                        style={{
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        }}
                      />
                    ))}
                  </colgroup>
                )}
                {showTableHead && (hasData || emptyStateShowHeaders) && (
                  <thead
                    className={twx('border-background-subtle border-b border-solid', {
                      'sticky top-0 z-10 bg-background-main': stickyHeaders && !isStacked,
                      hidden:
                        !showTableHead ||
                        isStacked ||
                        !columns.some(
                          (column) => column.title && !isEmpty(`${column.title}`.trim()),
                        ),
                    })}
                  >
                    <tr>
                      {columns.map((column, index) => (
                        <th
                          className={twx(
                            'group text-left font-medium text-content-subtle text-sm uppercase tracking-wider',
                            {
                              'px-4 py-3': spacing === 'relaxed',
                              'px-2 py-3': spacing === 'tight',
                              'text-right': index === 0 && rowHeaders && rowHeaders.length > 0,
                              'overflow-hidden text-ellipsis whitespace-nowrap':
                                stickyHeaders && !isStacked,
                              'whitespace-break-spaces break-normal': !stickyHeaders || isStacked,
                            },
                          )}
                          // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
                          key={`table-thead-th-${id}-${title}-${onlyText(column.title)}-${index}`}
                          scope="col"
                        >
                          <span
                            className={twx(
                              { 'sr-only': column.titleVisuallyHidden },
                              column.headerClassName,
                            )}
                          >
                            {column.title}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody className={twx({ 'h-[7rem]': records.length === 0 })}>
                  <LegacyTableRows
                    id={id}
                    title={title.toString()}
                    getRowClassName={getRowClassName}
                    records={records.slice(0, count)}
                    handleRowClick={handleRowClick}
                    canClickRow={canClickRow}
                    onCmdOrCtrlClick={onCmdOrCtrlClick}
                    RowActions={RowActions}
                    columns={columns}
                    loading={loading}
                    emptyMessage={emptyMessage}
                    isStacked={isStacked}
                    stackedColumnHeaders={stackedColumnHeaders}
                    rowHeaders={rowHeaders}
                    spacing={spacing}
                  />
                </tbody>
              </table>
            </div>
          </div>
          {!loading && pagination?.type === 'show-more' && (
            <ExpandList
              className={tw`items-center justify-between gap-2.5 pr-4 pl-4`}
              total={records.length}
              count={count}
              pageSize={pagination.pageSize}
              changeCount={setCount}
            />
          )}
          {!loading && pagination?.type === 'next-previous' && (
            <SimplePagination
              currentPage={internalCurrentPage}
              setCurrentPage={setCurrentPage}
              hasNext={pagination.hasNext}
            />
          )}
          {!loading && pagination?.type === 'pages' && (
            <Pagination
              currentPage={internalCurrentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pagination.pageSize}
              total={pagination.total}
            />
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

export type LegacyRowActionsConfigProp<T extends LegacyMinRecordItem> =
  | Array<{
      text: string;
      onClick: (record: T, onSuccess?: () => void) => VoidValue;
      className: ClassName;
      disabled?: boolean;
      testId?: string;
      render?: LegacyRowActionsProp<T>;
    }>
  | undefined;

export interface LegacyOnTableRowClickArgs<T extends LegacyMinRecordItem> {
  telemetry: ReturnType<typeof useTelemetry>;
  record: T;
}

type LegacyTableRowsProps<T extends LegacyMinRecordItem> = {
  id?: string;
  title: ReactNode;
  RowActions?: LegacyRowActionsProp<T>;
  columns: Array<LegacyTableColumn<T>>;
  emptyMessage: string | ReactElement;
  getRowClassName?: (record: T) => ClassName;
  handleRowClick?: (args: LegacyOnTableRowClickArgs<T>) => void;
  canClickRow?: (record: T) => boolean;
  onCmdOrCtrlClick?: (record: T) => void;
  loading: boolean;
  records: Array<T>;
  isStacked: boolean;
  stackedColumnHeaders: boolean;
  rowHeaders?: Array<string>;
  spacing: LegacyTableSpacing;
};

const LegacyTableRows = <T extends LegacyMinRecordItem>({
  id,
  title,
  records,
  columns,
  loading,
  emptyMessage,
  handleRowClick,
  canClickRow = () => true,
  onCmdOrCtrlClick,
  RowActions,
  getRowClassName,
  isStacked,
  stackedColumnHeaders,
  rowHeaders,
  spacing,
}: LegacyTableRowsProps<T>) => {
  if (loading) {
    return (
      <tr
        className={twx(
          'flex flex-col items-center justify-center p-6 align-top text-background-inverse [&:not(:last-child)]:border-0 [&:not(:last-child)]:border-background-subtle [&:not(:last-child)]:border-b [&:not(:last-child)]:border-solid',
          { 'relative p-3': isStacked },
        )}
      >
        <td
          className={tw`whitespace-pre-wrap px-3 py-4 text-background-inverse text-sm`}
          colSpan={columns.length}
        >
          <LoadingSpinner centered={true} message="Loading..." />
        </td>
      </tr>
    );
  }

  if (records.length === 0) {
    return (
      <tr className={twx({ 'relative block p-3': isStacked })}>
        <td
          className={tw`whitespace-pre-wrap px-3 py-4 text-background-inverse text-sm`}
          colSpan={columns.length}
        >
          <div
            className={tw`pointer-events-none flex h-full min-h-full w-full items-center justify-center text-center text-content-subtle`}
          >
            {emptyMessage}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {records.map((record, index) => (
        <TelemetryBoundary
          // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
          key={`table-tr-${id}-${title}-${record.key}-${index}`}
          boundaryName="TableRow"
          initialContext={
            isFhirResource(record)
              ? { tableRowResourceType: record.resourceType, tableRowResourceId: record.id }
              : {
                  /* For tables where we do not know the data type, we CANNOT pass any telemetry context here because it might contain PHI. */
                }
          }
        >
          {({ telemetry }) => (
            <tr
              className={twx(
                'group relative mx-px table-row border-gray-100 border-b align-top',
                isFunction(getRowClassName) ? getRowClassName(record) : '',
                {
                  'cursor-pointer hover:bg-background-hover':
                    isFunction(handleRowClick) && canClickRow(record),
                  'relative block p-3': isStacked,
                },
              )}
              onClick={({ metaKey, ctrlKey }) => {
                if (metaKey || ctrlKey) {
                  telemetry.trackInteraction('table_row_cmd_or_ctrl_click');
                  onCmdOrCtrlClick?.(record);
                } else {
                  telemetry.trackInteraction('table_row_left_click');
                  handleRowClick?.({ telemetry, record });
                }
              }}
            >
              {rowHeaders && rowHeaders.length > 0 && (
                <th
                  scope="row"
                  className={twx(
                    'group w-0 whitespace-break-spaces break-normal text-left font-medium text-content-subtle text-sm uppercase tracking-wider',
                    {
                      'px-4 py-3': spacing === 'relaxed',
                      'p-2': spacing === 'tight',
                      'block w-full p-0 pr-7': isStacked,
                    },
                  )}
                >
                  {rowHeaders[index]}
                </th>
              )}
              {columns.map((column, columnIndex) => (
                <TelemetryBoundary
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
                  key={`table-cell-${id}-${title}-${onlyText(column.title)}-${record.key}-${columnIndex}`}
                  boundaryName="TableCell"
                  initialContext={{ tableCellColumnTitle: onlyText(column.title) }}
                >
                  {() => (
                    <td
                      className={twx(
                        'whitespace-pre-wrap break-words text-gray-900 text-sm',
                        {
                          'p-4': spacing === 'relaxed',
                          'p-2': spacing === 'tight',
                          'block w-full p-0 pr-7': isStacked,
                        },
                        column.className,
                        column.render ? column.renderClassName?.(record) : undefined,
                      )}
                      style={{
                        minWidth: column.minWidth,
                        width: column.width,
                        maxWidth: column.maxWidth,
                      }}
                    >
                      {isStacked && stackedColumnHeaders ? (
                        <div className={tw`flex flex-col`}>
                          <div
                            className={tw`pt-2 text-left font-medium text-content-subtle text-sm uppercase tracking-wider`}
                          >
                            {column.title}:
                          </div>
                          <div>
                            {column.render
                              ? column.render(record, isStacked)
                              : (record[column.dataIndex as never] as never)}
                          </div>
                        </div>
                      ) : column.render ? (
                        column.render(record, isStacked)
                      ) : (
                        (record[column.dataIndex as never] as never)
                      )}
                    </td>
                  )}
                </TelemetryBoundary>
              ))}
              {RowActions && (
                <td
                  aria-label="row-actions"
                  className={twx(
                    'invisible absolute top-1 right-0 flex h-fit items-center space-x-2 whitespace-pre-wrap bg-table-actions-gradient px-4 text-background-inverse text-sm group-hover:visible',
                    {
                      'mt-0 w-fit justify-end px-4 pr-7': isStacked,
                    },
                  )}
                >
                  <div onClick={(event) => event.stopPropagation()}>
                    <RowActions record={record} stacked={isStacked} />
                  </div>
                </td>
              )}
            </tr>
          )}
        </TelemetryBoundary>
      ))}
    </>
  );
};
